import Papa from "papaparse";

const isPresent = (value) => value !== null && value !== undefined && value !== "";

const toKey = (value) => String(value);

const getFirstPresentValue = (...values) =>
  values.find((value) => isPresent(value)) ?? "";

export const normalizeCsvCell = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value !== "string") {
    return value;
  }

  return value.replace(/\r\n|\n|\r/g, " ");
};

export const normalizeCsvHeader = (value) => {
  if (value === null || value === undefined) {
    return "";
  }

  if (typeof value !== "string") {
    return String(value);
  }

  return value
    .replace(/[\u2018\u2019\u2032]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/\u00A0/g, " ")
    .trim();
};

const normalizeText = (value) => normalizeCsvHeader(value).toLowerCase();

const normalizeQuestionStructure = (questionSource) => {
  if (!questionSource) {
    return null;
  }

  if (
    Array.isArray(questionSource?.dimensions) ||
    Array.isArray(questionSource?.comments)
  ) {
    return questionSource;
  }

  if (questionSource?.questions) {
    return normalizeQuestionStructure(questionSource.questions);
  }

  return null;
};

const createUniqueHeader = (baseHeader, usedHeaders, fallbackHeader) => {
  const normalizedBase = normalizeCsvHeader(baseHeader || fallbackHeader);
  let header = normalizedBase || fallbackHeader;

  if (!usedHeaders.has(header)) {
    usedHeaders.add(header);
    return header;
  }

  let index = 2;
  let candidate = `${header} (${index})`;
  while (usedHeaders.has(candidate)) {
    index += 1;
    candidate = `${header} (${index})`;
  }

  usedHeaders.add(candidate);
  return candidate;
};

const refsFor = (...values) =>
  values
    .flat()
    .filter((value) => isPresent(value))
    .map((value) => toKey(value));

const getSortableOrder = (value, fallback) => {
  const candidate = getFirstPresentValue(value, fallback);
  const numericCandidate = Number(candidate);

  return Number.isFinite(numericCandidate) ? numericCandidate : fallback;
};

const getQuestionResponseOrder = (question, fallbackOrder) =>
  getFirstPresentValue(
    question?.order,
    question?.ques_order,
    question?.id,
    fallbackOrder
  );

const getStructureQuestionCount = (structure) =>
  (structure?.dimensions || []).reduce(
    (dimensionTotal, dimension) =>
      dimensionTotal +
      (dimension?.factors || []).reduce(
        (factorTotal, factor) => factorTotal + (factor?.questions || []).length,
        0
      ),
    0
  );

const getCommentId = (comment) =>
  getFirstPresentValue(comment?.id, comment?.question_id, comment?.qid);

const getCommentOrder = (comment) =>
  getFirstPresentValue(comment?.order, comment?.ques_order, comment?.ques_sort_order);

const getCommentText = (comment) =>
  getFirstPresentValue(comment?.question, comment?.q, comment?.title);

const getDuplicateCommentHeader = (comment, duplicateHeaderCounts) => {
  const headerText = getCommentText(comment);
  const normalizedHeader = normalizeCsvHeader(headerText);

  if ((duplicateHeaderCounts[normalizedHeader] || 0) <= 1) {
    return headerText;
  }

  const order = getCommentOrder(comment);
  if (isPresent(order)) {
    return `${headerText} (Order ${order})`;
  }

  const id = getCommentId(comment);
  if (isPresent(id)) {
    return `${headerText} (ID ${id})`;
  }

  return headerText;
};

const getCommentKey = (comment, fallbackIndex) => {
  const id = getCommentId(comment);
  if (isPresent(id)) {
    return `id:${toKey(id)}`;
  }

  const order = getCommentOrder(comment);
  if (isPresent(order)) {
    return `order:${toKey(order)}`;
  }

  const text = normalizeText(getCommentText(comment));
  if (text) {
    return `text:${text}`;
  }

  return `index:${fallbackIndex}`;
};

const mergeComments = (...commentLists) => {
  const comments = [];
  const seen = new Set();

  commentLists.flat().forEach((comment, index) => {
    if (!comment) {
      return;
    }

    const key = getCommentKey(comment, index);
    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    comments.push(comment);
  });

  return comments;
};

export const resolveOtpQuestionStructure = ({
  coreData,
  surveyDefinitions,
  selectedSortOrder,
  surveyVersion,
  selectedSurvey,
}) => {
  const selectedSurveyId = getFirstPresentValue(
    surveyVersion?.id,
    selectedSurvey?.question?.id
  );
  const matchingSurveyDefinition = (surveyDefinitions || []).find(
    (surveyDefinition) =>
      Number(surveyDefinition?.sort_order) === Number(selectedSortOrder) ||
      (isPresent(selectedSurveyId) &&
        toKey(surveyDefinition?.id) === toKey(selectedSurveyId))
  );
  const surveyDefinitionStructures = (surveyDefinitions || [])
    .map(normalizeQuestionStructure)
    .filter(Boolean);
  const candidates = [
    matchingSurveyDefinition,
    surveyVersion,
    selectedSurvey?.question,
    coreData?.questions,
    surveyDefinitions?.[0],
  ]
    .map(normalizeQuestionStructure)
    .filter(Boolean);

  const bestStructure = candidates.reduce((best, candidate) => {
    if (!best) {
      return candidate;
    }

    const bestScore =
      (best?.comments || []).length * 1000 + getStructureQuestionCount(best);
    const candidateScore =
      (candidate?.comments || []).length * 1000 +
      getStructureQuestionCount(candidate);

    return candidateScore > bestScore ? candidate : best;
  }, null);

  if (!bestStructure) {
    return null;
  }

  return {
    ...bestStructure,
    comments: mergeComments(
      ...candidates.map((candidate) => candidate.comments || []),
      ...surveyDefinitionStructures.map((candidate) => candidate.comments || [])
    ),
  };
};

export const flattenOtpQuestions = (questionStructure) => {
  const structure = normalizeQuestionStructure(questionStructure);
  const usedHeaders = new Set();
  const questions = [];
  let fallbackOrder = 1;

  (structure?.dimensions || []).forEach((dimension, dimensionIndex) => {
    (dimension?.factors || []).forEach((factor, factorIndex) => {
      (factor?.questions || []).forEach((question, questionIndex) => {
        const responseOrder = getQuestionResponseOrder(question, fallbackOrder);
        const questionText =
          question?.q || question?.question || question?.title || "";
        const fallbackHeader = `Question ${fallbackOrder}`;

        questions.push({
          header: createUniqueHeader(questionText, usedHeaders, fallbackHeader),
          overallOrder: responseOrder,
          sortOrder: getSortableOrder(responseOrder, fallbackOrder),
          dimensionIndex,
          factorIndex,
          questionIndex,
          dimensionRefs: refsFor(dimensionIndex, dimension?.id, dimensionIndex + 1),
          factorRefs: refsFor(factorIndex, factor?.id, factorIndex + 1),
          questionRefs: refsFor(
            questionIndex,
            question?.id,
            question?.q_id,
            questionIndex + 1
          ),
          question,
          factor,
          dimension,
        });

        fallbackOrder += 1;
      });
    });
  });

  return questions.sort((a, b) => a.sortOrder - b.sortOrder);
};

export const flattenOtpComments = (questionStructure) => {
  const structure = normalizeQuestionStructure(questionStructure);
  const usedHeaders = new Set();
  const comments = [...(structure?.comments || [])].sort(
    (a, b) =>
      getSortableOrder(getCommentOrder(a), Number.POSITIVE_INFINITY) -
      getSortableOrder(getCommentOrder(b), Number.POSITIVE_INFINITY)
  );
  const duplicateHeaderCounts = comments.reduce((counts, comment) => {
    const header = normalizeCsvHeader(getCommentText(comment));
    if (!header) {
      return counts;
    }

    return {
      ...counts,
      [header]: (counts[header] || 0) + 1,
    };
  }, {});

  return comments.map((comment, index) => ({
    header: createUniqueHeader(
      getDuplicateCommentHeader(comment, duplicateHeaderCounts),
      usedHeaders,
      `Comment ${index + 1}`
    ),
    index,
    id: getCommentId(comment),
    order: getCommentOrder(comment),
    question: comment,
  }));
};

const responseDimensionValue = (responseQuestion) =>
  getFirstPresentValue(
    responseQuestion?.dimension,
    responseQuestion?.id,
    responseQuestion?.d
  );

const responseFactorValue = (responseQuestion) =>
  getFirstPresentValue(
    responseQuestion?.factor,
    responseQuestion?.section,
    responseQuestion?.f
  );

const responseQuestionValue = (responseQuestion) =>
  getFirstPresentValue(
    responseQuestion?.q,
    responseQuestion?.question_id,
    responseQuestion?.qid
  );

const hasMatchingRef = (value, refs) =>
  isPresent(value) && refs.some((ref) => ref === toKey(value));

const matchesQuestionRefs = (
  responseQuestion,
  dimensionRefs,
  factorRefs,
  questionRefs
) =>
  hasMatchingRef(responseDimensionValue(responseQuestion), dimensionRefs) &&
  hasMatchingRef(responseFactorValue(responseQuestion), factorRefs) &&
  hasMatchingRef(responseQuestionValue(responseQuestion), questionRefs);

const getResponseValue = (responseQuestion) => responseQuestion?.response ?? "";

const hasOtpQuestionTuple = (responseQuestion) =>
  isPresent(responseQuestion?.id) &&
  isPresent(responseQuestion?.factor) &&
  isPresent(responseQuestion?.q);

const matchesOtpQuestionTuple = (responseQuestion, questionDefinition) =>
  toKey(responseQuestion?.id) === toKey(questionDefinition.dimensionIndex) &&
  toKey(responseQuestion?.factor) === toKey(questionDefinition.factorIndex) &&
  toKey(responseQuestion?.q) === toKey(questionDefinition.questionIndex);

export const findOtpQuestionResponseValue = (
  questionResponses,
  questionDefinition
) => {
  if (!Array.isArray(questionResponses) || !questionDefinition) {
    return "";
  }

  const tupleResponses = questionResponses.filter(hasOtpQuestionTuple);

  if (tupleResponses.length > 0) {
    const matchedTupleResponse = tupleResponses.find((responseQuestion) =>
      matchesOtpQuestionTuple(responseQuestion, questionDefinition)
    );

    return matchedTupleResponse ? getResponseValue(matchedTupleResponse) : "";
  }

  const matchAttempts = [
    {
      dimensionRefs: refsFor(questionDefinition.dimensionIndex),
      factorRefs: refsFor(questionDefinition.factorIndex),
      questionRefs: refsFor(questionDefinition.questionIndex),
    },
    {
      dimensionRefs: refsFor(questionDefinition.dimension?.id),
      factorRefs: refsFor(questionDefinition.factorIndex),
      questionRefs: refsFor(
        questionDefinition.questionIndex,
        questionDefinition.question?.id
      ),
    },
    {
      dimensionRefs: refsFor(questionDefinition.dimensionIndex),
      factorRefs: refsFor(questionDefinition.factor?.id),
      questionRefs: refsFor(
        questionDefinition.questionIndex,
        questionDefinition.question?.id
      ),
    },
    {
      dimensionRefs: refsFor(questionDefinition.dimension?.id),
      factorRefs: refsFor(questionDefinition.factor?.id),
      questionRefs: refsFor(questionDefinition.question?.id),
    },
    {
      dimensionRefs: refsFor(
        questionDefinition.dimensionIndex + 1,
        questionDefinition.dimension?.id
      ),
      factorRefs: refsFor(
        questionDefinition.factorIndex + 1,
        questionDefinition.factor?.id
      ),
      questionRefs: refsFor(
        questionDefinition.questionIndex + 1,
        questionDefinition.question?.id
      ),
    },
    {
      dimensionRefs: questionDefinition.dimensionRefs,
      factorRefs: questionDefinition.factorRefs,
      questionRefs: questionDefinition.questionRefs,
    },
  ];

  for (const attempt of matchAttempts) {
    const matchedResponse = questionResponses.find((responseQuestion) =>
      matchesQuestionRefs(
        responseQuestion,
        attempt.dimensionRefs,
        attempt.factorRefs,
        attempt.questionRefs
      )
    );

    if (matchedResponse) {
      return getResponseValue(matchedResponse);
    }
  }

  const orderRefs = refsFor(
    questionDefinition.overallOrder,
    questionDefinition.question?.order
  );
  const orderMatchedResponse = questionResponses.find((responseQuestion) =>
    hasMatchingRef(
      getFirstPresentValue(
        responseQuestion?.ques_order,
        responseQuestion?.order,
        responseQuestion?.quesOrder
      ),
      orderRefs
    )
  );

  return orderMatchedResponse ? getResponseValue(orderMatchedResponse) : "";
};

const getCommentValue = (comment) =>
  normalizeCsvCell(
    getFirstPresentValue(comment?.response, comment?.feedback, comment?.comment)
  );

const findMatchingCommentDefinition = (
  comment,
  commentDefinitions,
  index,
  options = {}
) => {
  const commentId = getFirstPresentValue(
    comment?.id,
    comment?.question_id,
    comment?.qid
  );

  if (isPresent(commentId)) {
    const match = commentDefinitions.find(
      (definition) => toKey(definition.id) === toKey(commentId)
    );
    if (match) {
      return match;
    }
  }

  const commentOrder = getFirstPresentValue(
    comment?.ques_order,
    comment?.ques_sort_order,
    comment?.order
  );

  if (isPresent(commentOrder)) {
    const match = commentDefinitions.find(
      (definition) => toKey(definition.order) === toKey(commentOrder)
    );
    if (match) {
      return match;
    }

    if (isPresent(commentId)) {
      return null;
    }
  } else if (isPresent(commentId)) {
    return null;
  }

  const commentQuestion = normalizeText(
    getFirstPresentValue(comment?.question, comment?.q, comment?.title)
  );

  if (commentQuestion) {
    const match = commentDefinitions.find((definition) => {
      const definitionQuestion = normalizeText(
        getFirstPresentValue(
          definition.question?.question,
          definition.question?.q,
          definition.question?.title
        )
      );

      return (
        definitionQuestion &&
        (definitionQuestion === commentQuestion ||
          definitionQuestion.includes(commentQuestion) ||
          commentQuestion.includes(definitionQuestion))
      );
    });

    if (match) {
      return match;
    }
  }

  return options.allowIndexFallback ? commentDefinitions[index] || null : null;
};

const findMatchingResponseComment = (comments, commentDefinition) => {
  if (!Array.isArray(comments) || !commentDefinition) {
    return null;
  }

  const definitionId = getFirstPresentValue(
    commentDefinition?.id,
    commentDefinition?.question?.id,
    commentDefinition?.question?.question_id,
    commentDefinition?.question?.qid
  );

  if (isPresent(definitionId)) {
    const match = comments.find(
      (comment) => toKey(getCommentId(comment)) === toKey(definitionId)
    );
    if (match) {
      return match;
    }
  }

  const definitionOrder = getFirstPresentValue(
    commentDefinition?.order,
    commentDefinition?.question?.order,
    commentDefinition?.question?.ques_order,
    commentDefinition?.question?.ques_sort_order
  );

  if (isPresent(definitionOrder)) {
    const match = comments.find(
      (comment) => toKey(getCommentOrder(comment)) === toKey(definitionOrder)
    );
    if (match) {
      return match;
    }

    if (isPresent(definitionId)) {
      return null;
    }
  } else if (isPresent(definitionId)) {
    return null;
  }

  const definitionQuestion = normalizeText(
    getFirstPresentValue(
      commentDefinition?.question?.question,
      commentDefinition?.question?.q,
      commentDefinition?.question?.title,
      commentDefinition?.header
    )
  );

  if (definitionQuestion) {
    const match = comments.find((comment) => {
      const commentQuestion = normalizeText(getCommentText(comment));
      return (
        commentQuestion &&
        (commentQuestion === definitionQuestion ||
          commentQuestion.includes(definitionQuestion) ||
          definitionQuestion.includes(commentQuestion))
      );
    });

    if (match) {
      return match;
    }
  }

  return null;
};

const buildOtpCommentDefinitions = (questionStructure, responses) => {
  const definitionComments = flattenOtpComments(questionStructure);
  const usedHeaders = new Set(definitionComments.map((comment) => comment.header));
  const comments = [...definitionComments];

  (responses || []).forEach((responseEntry) => {
    const response = normalizeOtpResponse(responseEntry);
    (response?.comments || []).forEach((comment, index) => {
      const existingDefinition = findMatchingCommentDefinition(
        comment,
        comments,
        index
      );

      if (existingDefinition) {
        return;
      }

      comments.push({
        header: createUniqueHeader(
          getCommentText(comment),
          usedHeaders,
          `Comment ${comments.length + 1}`
        ),
        index: comments.length,
        id: getCommentId(comment),
        order: getCommentOrder(comment),
        question: comment,
      });
    });
  });

  return comments.sort(
    (a, b) =>
      getSortableOrder(a?.order, Number.POSITIVE_INFINITY) -
      getSortableOrder(b?.order, Number.POSITIVE_INFINITY)
  );
};

export const buildOtpCategoryColumns = (
  response,
  categoryDefinitions,
  usedHeaders = new Set()
) => {
  const columns = {};

  (categoryDefinitions || []).forEach((categoryDefinition, index) => {
    const baseHeader =
      categoryDefinition?.name || categoryDefinition?.title || categoryDefinition?.id;
    const header = createUniqueHeader(
      baseHeader,
      usedHeaders,
      `Category ${index + 1}`
    );
    const matchedCategory = response?.categories?.find(
      (category) => category?.id === categoryDefinition?.id
    );
    const option = categoryDefinition?.options?.find(
      (item) => item?.id === matchedCategory?.response
    );

    columns[header] = option?.name || matchedCategory?.response || "";

    const hasSubfactors = categoryDefinition?.options?.some((item) =>
      Array.isArray(item?.subfactors)
    );

    if (hasSubfactors) {
      const subfactorHeader = createUniqueHeader(
        `${header} Subfactor`,
        usedHeaders,
        `Category ${index + 1} Subfactor`
      );
      const subfactor = option?.subfactors?.find(
        (item) => item?.id === matchedCategory?.subfactor
      );
      columns[subfactorHeader] = subfactor?.name || matchedCategory?.subfactor || "";
    }
  });

  return columns;
};

export const normalizeOtpResponse = (responseEntry) => {
  const response = responseEntry?.response || responseEntry || {};
  const employeeId = getFirstPresentValue(
    responseEntry?.employee,
    responseEntry?.employee_id,
    response?.employee,
    response?.employee_id
  );

  return {
    ...response,
    employee: employeeId || response?.employee,
  };
};

export const buildLatestOtpResponsesFromReports = (
  cultureAuditReports,
  selectedSortOrder
) => {
  const reports = Array.isArray(cultureAuditReports) ? cultureAuditReports : [];
  const filteredReports = selectedSortOrder
    ? reports.filter(
        (report) =>
          Number(report?.question_sort_order) === Number(selectedSortOrder) ||
          Number(report?.question?.sort_order) === Number(selectedSortOrder)
      )
    : reports;
  const sortedReports = [...filteredReports].sort((a, b) => b.id - a.id);
  const latestDateByDataset = {};

  return sortedReports
    .filter((report) => {
      const key = report?.name_uid || report?.name || report?.id;
      if (!latestDateByDataset[key]) {
        latestDateByDataset[key] = report?.date;
      }

      return report?.date === latestDateByDataset[key];
    })
    .flatMap((report) => report?.responses || [])
    .map((responseEntry) =>
      normalizeOtpResponse({
        ...responseEntry,
        employee: responseEntry?.employee_id,
      })
    )
    .filter((response) => response && !Array.isArray(response));
};

export const selectOtpExportResponses = ({
  rawData,
  cultureAuditReports,
  selectedSortOrder,
}) => {
  const latestRawResponses = Array.isArray(rawData)
    ? rawData?.[0]?.responses
    : rawData?.responses;

  if (Array.isArray(latestRawResponses) && latestRawResponses.length > 0) {
    return latestRawResponses.map(normalizeOtpResponse);
  }

  const reportResponses = buildLatestOtpResponsesFromReports(
    cultureAuditReports,
    selectedSortOrder
  );

  if (reportResponses.length > 0) {
    return reportResponses;
  }

  return [];
};

export const buildOtpExport = ({
  responses,
  questionStructure,
  categoryDefinitions,
  emailByEmployeeId,
}) => {
  const normalizedResponses = (responses || []).map(normalizeOtpResponse);
  const questionDefinitions = flattenOtpQuestions(questionStructure);
  const commentDefinitions = buildOtpCommentDefinitions(
    questionStructure,
    normalizedResponses
  );
  const headerKeys = ["Member Email", "Employee ID"];
  const categoryHeaderSet = new Set();
  const categoryHeaders = Object.keys(
    buildOtpCategoryColumns({}, categoryDefinitions, categoryHeaderSet)
  );

  headerKeys.push(...categoryHeaders);
  headerKeys.push(...questionDefinitions.map((question) => question.header));
  headerKeys.push(...commentDefinitions.map((comment) => comment.header));

  const rows = normalizedResponses.map((response) => {
    const employeeId = response?.employee;
    const row = {
      "Member Email": employeeId ? emailByEmployeeId?.[employeeId] || "" : "",
      "Employee ID": employeeId || "",
    };
    const categoryColumns = buildOtpCategoryColumns(
      response,
      categoryDefinitions,
      new Set()
    );

    categoryHeaders.forEach((header) => {
      row[header] = categoryColumns[header] || "";
    });

    questionDefinitions.forEach((questionDefinition) => {
      row[questionDefinition.header] = findOtpQuestionResponseValue(
        response?.questions,
        questionDefinition
      );
    });

    commentDefinitions.forEach((commentDefinition) => {
      const matchingComment = findMatchingResponseComment(
        response?.comments,
        commentDefinition
      );
      row[commentDefinition.header] = matchingComment
        ? getCommentValue(matchingComment)
        : "";
    });

    return row;
  });

  return {
    rows,
    headerKeys,
    questionDefinitions,
    commentDefinitions,
  };
};

export const buildCsvContent = (rows, headerKeys) => {
  const csv = Papa.unparse({
    fields: headerKeys,
    data: (rows || []).map((row) =>
      headerKeys.map((header) => normalizeCsvCell(row?.[header]))
    ),
  });

  return `\uFEFF${csv}`;
};

export const buildOtpExportFilename = (selectedSortOrder, suffix) => {
  const suffixPart = suffix ? `_${suffix}` : "";
  return `otp_raw_export_survey-sort-order-${
    selectedSortOrder || "unknown"
  }${suffixPart}.csv`;
};
